import { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import {
  buildRateCardConditionChips,
  RateCardConditionTone,
} from '~components/RateCard/rateCardConditionChips';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { deriveScheduleLabel } from '~utils/deriveScheduleLabel';
import { formatRateWithType } from '~utils/rateFormatting';

/**
 * Read-only summary of one rate card (SiteRateSchedule) — its match conditions, its name,
 * and the three party rates. Shared by every surface that needs to show a rate card without
 * opening a form: the order card, the project card, and the job row provenance tooltip.
 *
 * Presentational only. Callers own fetching and the popover/tooltip shell.
 */

// A rate is omitted rather than shown blank when a card sets no rate for that party —
// a customer-only card is normal, and empty rows would imply a rate of zero.
const NO_RATE_PLACEHOLDER = '';

// Condition tones resolve to the categorical chip tokens — saturated enough to read as
// chips, and defined per theme mode. The `status*Bg` washes are deliberately NOT used here:
// they are severity indicators, too faint to see, and `statusWarningBg` is derived from the
// error hue, which made equipment types look like failures.
const CONDITION_TONE_BACKGROUNDS: Record<
  RateCardConditionTone,
  (theme: Theme) => string
> = {
  info: (theme) => theme.semantic.chipInfoBg,
  success: (theme) => theme.semantic.chipSuccessBg,
  warning: (theme) => theme.semantic.chipWarningBg,
};

interface RateCardSummaryProps {
  rateCard: SiteRateSchedule;
}

export default function RateCardSummary({ rateCard }: RateCardSummaryProps) {
  const { currencyFormatter } = useCompanyCurrency();

  const partyRates = [
    {
      label: t('order.form.customer_rate'),
      value: formatRateWithType(
        rateCard.customerRateValue,
        rateCard.customerRateType,
        currencyFormatter,
        { emptyValue: NO_RATE_PLACEHOLDER, unitStyle: 'short' },
      ),
    },
    {
      label: t('order.form.vendor_rate'),
      value: formatRateWithType(
        rateCard.vendorRateValue,
        rateCard.vendorRateType,
        currencyFormatter,
        { emptyValue: NO_RATE_PLACEHOLDER, unitStyle: 'short' },
      ),
    },
    {
      label: t('order.form.driver_rate'),
      value: formatRateWithType(
        rateCard.driverRateValue,
        rateCard.driverRateType,
        currencyFormatter,
        { emptyValue: NO_RATE_PLACEHOLDER, unitStyle: 'short' },
      ),
    },
  ].filter((partyRate) => partyRate.value !== NO_RATE_PLACEHOLDER);

  const conditionChips = buildRateCardConditionChips(rateCard);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {/* Conditions lead the row so the eye scans the match dimensions first, with the
          card's name wrapping alongside them rather than truncating. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 0.5 }}>
        {conditionChips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            size="small"
            sx={(theme) => ({
              backgroundColor: CONDITION_TONE_BACKGROUNDS[chip.tone](theme),
              borderRadius: 1,
              color: theme.semantic.textPrimary,
              fontSize: '0.6875rem',
              fontWeight: 700,
              height: '1.25rem',
            })}
          />
        ))}
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '0.8125rem',
            fontWeight: 600,
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
          })}
        >
          {deriveScheduleLabel(rateCard)}
        </Typography>
      </Box>

      {partyRates.length > 0 && (
        // Both columns size to their content and sit hard left, so each value reads
        // immediately after its label instead of across a gap the eye has to track.
        // The label column still auto-sizes to the widest label, which keeps the values
        // in a single scannable column.
        <Box
          sx={{
            columnGap: 1.5,
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            justifyContent: 'start',
            rowGap: 0.25,
          }}
        >
          {partyRates.map((partyRate) => (
            <Box key={partyRate.label} sx={{ display: 'contents' }}>
              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray1,
                  fontSize: '0.75rem',
                })}
              >
                {partyRate.label}
              </Typography>
              <Typography
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadBlack,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                })}
              >
                {partyRate.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
